import { useNavigate } from 'react-router-dom';
import { 
  Scale, 
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';

export const LegalPage = () => {
  const navigate = useNavigate();

  return (
    <div className="page-wrapper" data-testid="legal-page">
      {/* Header */}
      <div className="app-header">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/profile')}
            className="p-2 hover:bg-slate-800 rounded-lg"
          >
            <ArrowLeft className="text-slate-400" size={24} />
          </button>
          <div className="flex items-center gap-3">
            <Scale className="text-amber-500" size={28} />
            <div>
              <h1 className="text-2xl text-white">Mentions légales</h1>
              <p className="text-slate-400 text-sm">Avis important</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Warning Banner */}
        <div className="bg-amber-900/30 border border-amber-700 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-500 flex-shrink-0 mt-1" size={24} />
            <div>
              <h2 className="text-amber-400 font-bold text-lg">Avis important</h2>
              <p className="text-amber-300 text-sm mt-2">
                Cette application est un <strong>outil d'aide à la gestion</strong> pour les chauffeurs professionnels. 
                Elle ne remplace en aucun cas les lois et règlements en vigueur.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-6">
          {/* Ce que l'app est */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="text-green-500" size={20} />
              <h3 className="text-white font-bold">Cette application est:</h3>
            </div>
            <ul className="text-slate-300 text-sm space-y-2 ml-7">
              <li>• Un outil d'aide à la gestion des heures de service</li>
              <li>• Un assistant pour le suivi des inspections pré-départ</li>
              <li>• Un système de documentation des rapports journaliers</li>
              <li>• Une aide à la conformité réglementaire</li>
            </ul>
          </div>

          {/* Ce que l'app n'est pas */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <XCircle className="text-red-500" size={20} />
              <h3 className="text-white font-bold">Cette application NE remplace PAS:</h3>
            </div>
            <ul className="text-slate-300 text-sm space-y-2 ml-7">
              <li>• Le Règlement sur les heures de conduite et de repos (SAAQ)</li>
              <li>• Le Code de la sécurité routière du Québec</li>
              <li>• Les règlements fédéraux sur le transport routier</li>
              <li>• Les registres officiels requis par la loi</li>
              <li>• Le jugement professionnel du conducteur</li>
            </ul>
          </div>

          {/* Responsabilité */}
          <div className="border-t border-slate-800 pt-4">
            <h3 className="text-white font-bold mb-3">Responsabilité du conducteur</h3>
            <p className="text-slate-400 text-sm">
              Le conducteur demeure entièrement responsable du respect des lois et règlements applicables. 
              Il est de sa responsabilité de:
            </p>
            <ul className="text-slate-400 text-sm space-y-2 mt-3 ml-4">
              <li>• Vérifier l'exactitude des informations saisies</li>
              <li>• S'assurer de la conformité aux règlements en vigueur</li>
              <li>• Maintenir les registres officiels requis</li>
              <li>• Consulter les autorités compétentes en cas de doute</li>
            </ul>
          </div>

          {/* Références légales */}
          <div className="border-t border-slate-800 pt-4">
            <h3 className="text-white font-bold mb-3">Références légales</h3>
            <ul className="text-slate-400 text-sm space-y-2">
              <li>
                <a 
                  href="https://saaq.gouv.qc.ca/transport-biens/heures-conduite-repos-conducteur" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  → SAAQ - Heures de conduite et repos
                </a>
              </li>
              <li>
                <a 
                  href="https://www.legisquebec.gouv.qc.ca/fr/document/lc/C-24.2" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  → Code de la sécurité routière
                </a>
              </li>
              <li>
                <a 
                  href="https://laws-lois.justice.gc.ca/fra/reglements/SOR-2005-313/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  → Règlement sur les heures de service (fédéral)
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-slate-800/50 rounded-xl p-4">
          <p className="text-slate-500 text-xs text-center">
            En utilisant cette application, vous reconnaissez avoir lu et compris ces mentions légales 
            et acceptez que l'application soit utilisée uniquement comme outil d'aide.
          </p>
        </div>

        {/* Version and Copyright */}
        <div className="text-center py-2">
          <p className="text-slate-600 text-xs">
            Dernière mise à jour: Mars 2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
